import { useState } from "react";
import DatePicker from "react-datepicker";
import { useCompany } from "../components/CompanySelector";
import { Partner, Product } from "../dto/commons";
import DataTable, { type Column } from "../components/DataTable";
import { formatCurrency, formatLocaleDate } from "../utils/helpers";
import { EmptyState, ErrorState } from "../components/Illustrations";
import useSWR from "swr";
import type { Branch } from "../dto/commons";
import { BranchSelector } from "../components/selector/BranchSelector";
import ProductSelector from "../components/selector/ProductSelector";
import { PartnerSelector } from "../components/selector/PartnerSelector";
import { cn } from "../utils/style";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { getStartOfMonth } from "../utils/helpers";
import { SalesReportQueryDto, SalesReportResultDto } from "../dto/reports.dto";
import {
  exportSalesReport,
  getSalesReport,
  getSalesStats,
} from "../services/sales.service";
import { RangeFilter, RangeTemplate } from "../components/Range";
import { RevenueCard } from "../components/sales/RevenueCard";
import { SalesCard } from "../components/sales/SalesCard";
import { Row } from "@tanstack/react-table";

const rangeOptions: RangeTemplate[] = [
  { id: "under-min-1000", label: "≦ Rp. -1000K", max: -1000000 },
  { id: "under-min-500", label: "≦ Rp. -500K", max: -500000 },
  { id: "under-min-100", label: "≦ Rp. -100K", max: -100000 },
  { id: "under-0", label: "≦ Rp. 0", max: 0 },
  { id: "0-100", label: "Rp. 0-Rp. 100K", min: 0, max: 100000 },
  {
    id: "100-500",
    label: "Rp. 100K-500K",
    min: 100000,
    max: 500000,
  },
  {
    id: "500-1000",
    label: "Rp. 500K-1000K",
    min: 500000,
    max: 1000000,
  },
  { id: "above-1000", label: "≧ Rp. 1000K", min: 1000000 },
];

const columns: Column<SalesReportResultDto>[] = [
  {
    header: "Sale Order",
    cell: ({ row }) => (
      <div className="font-medium">{row.original.sale_order_number}</div>
    ),
    minSize: 120,
  },
  {
    accessorFn: (row) =>
      row.sale_order_date ? formatLocaleDate(row.sale_order_date) : "-",
    header: "Order Date",
    minSize: 120,
  },
  {
    accessorKey: "invoice_number",
    header: "Invoice Number",
  },
  {
    accessorFn: (row) =>
      row.invoice_date ? formatLocaleDate(row.invoice_date) : "-",
    header: "Invoice Date",
    minSize: 120,
  },
  {
    accessorFn: (row) => `${row.product}`,
    header: "Product",
    minSize: 200,
  },
  {
    accessorFn: (row) => `${row.label}`,
    header: "Label",
    minSize: 200,
  },
  {
    header: "Account",
    accessorKey: "account",
    minSize: 150,
  },
  {
    header: "LOT / Serial Number",
    cell: ({ row }) => (
      <div className="flex flex-wrap items-center gap-2">
        {row.original.line_serial_number && (
          <div className="text-xs font-medium rounded-full px-2 py-1 bg-gray-200 border-gray-300 text-gray-800">
            {row.original.line_serial_number}
          </div>
        )}
      </div>
    ),
  },
  {
    accessorKey: "category",
    header: "Category",
    defaultHidden: true,
    id: "category",
  },
  {
    accessorKey: "uom",
    header: "UoM",
    defaultHidden: true,
    id: "uom",
  },
  {
    accessorFn: (row) => row.quantity,
    header: "Qty",
  },
  {
    header: "Price Unit",
    cell: ({ row }) => (
      <div className="font-semibold">
        {row.original.price && formatCurrency(row.original.price)}
      </div>
    ),
  },
  {
    header: "Subtotal",
    cell: ({ row }) => (
      <div className="font-semibold">
        {row.original.subtotal && formatCurrency(row.original.subtotal)}
      </div>
    ),
  },
  {
    header: "COGS",
    cell: ({ row }) => (
      <div className="font-semibold">
        {row.original.cogs && formatCurrency(row.original.cogs)}
      </div>
    ),
  },
  {
    header: "Profit",
    cell: ({ row }) => {
      const profit = row.original.profit ? parseFloat(row.original.profit) : 0;
      return (
        <div
          className={cn(
            "font-semibold whitespace-nowrap",
            profit < 0 ? "text-red-700" : "text-green-700",
          )}
        >
          {formatCurrency(profit)}
        </div>
      );
    },
  },
  {
    accessorKey: "customer",
    header: "Partner",
    minSize: 200,
  },
  {
    accessorKey: "customer_name",
    header: "Customer Name",
  },
  {
    accessorKey: "customer_phone",
    header: "Customer Phone",
  },
  {
    accessorKey: "salesperson",
    header: "Salesperson",
  },
  {
    accessorKey: "branch",
    header: "Branch",
    minSize: 200,
  },
  {
    accessorKey: "location",
    header: "Location",
  },
  {
    accessorKey: "vendor",
    header: "Vendor",
    minSize: 150,
  },

  {
    accessorKey: "memo",
    defaultHidden: true,
    header: "Memo",
    id: "memo",
  },
  {
    accessorKey: "note",
    defaultHidden: true,
    header: "Note",
    id: "note",
  },
];

export default function SalesReportPage() {
  const { selectedCompany } = useCompany();

  const [startDate, setStartDate] = useState<Date | null>(getStartOfMonth());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedBranches, setSelectedBranches] = useState<Branch[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<Partner[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<Partner[]>([]);
  const [selectedRange, setSelectedRange] = useState<
    | {
        min?: number | undefined;
        max?: number | undefined;
      }
    | undefined
  >(undefined);
  const [paginationState, setPaginationState] = useState<{
    pageSize: number;
    pageIndex: number;
  }>({
    pageSize: 10,
    pageIndex: 0,
  });

  const [exporting, setExporting] = useState(false);
  const [isFetched, setFetched] = useState(false);

  const { data, isLoading, error, mutate } = useSWR(
    isFetched && selectedCompany && startDate && endDate
      ? [
          "/reports/sales",
          {
            page: paginationState.pageIndex + 1,
            limit: paginationState.pageSize,
            companyId: selectedCompany.id,
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
            branchIds: selectedBranches.map((b) => b.id),
            vendorIds: selectedVendors.map((v) => v.id),
            productIds: selectedProducts.map((p) => p.id),
            partnerIds: selectedPartners.map((p) => p.id),
            minProfit: selectedRange?.min,
            maxProfit: selectedRange?.max,
          } as SalesReportQueryDto,
        ]
      : null,
    ([_, query]) => getSalesReport(query),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const { data: stats, isLoading: statsLoading } = useSWR(
    isFetched && selectedCompany && startDate && endDate
      ? [
          "/reports/sales/stats",
          {
            companyId: selectedCompany.id,
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
            branchIds: selectedBranches.map((b) => b.id),
            vendorIds: selectedVendors.map((v) => v.id),
            productIds: selectedProducts.map((p) => p.id),
            partnerIds: selectedPartners.map((p) => p.id),
            minProfit: selectedRange?.min,
            maxProfit: selectedRange?.max,
          } as SalesReportQueryDto,
        ]
      : null,
    ([_, query]) => getSalesStats(query),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const fetchData = async () => {
    setFetched(true);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      if (selectedCompany && startDate && endDate) {
        await exportSalesReport({
          companyId: selectedCompany.id,
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          branchIds: selectedBranches.map((b) => b.id),
          vendorIds: selectedVendors.map((v) => v.id),
          productIds: selectedProducts.map((p) => p.id),
          partnerIds: selectedPartners.map((p) => p.id),
          minProfit: selectedRange?.min,
          maxProfit: selectedRange?.max,
        });
      }
    } catch (e) {
      alert("Something went wrong went exporting data");
      console.log(e);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    startDate,
    endDate,
    selectedBranches,
    selectedProducts,
    selectedVendors,
    selectedRange,
  ]);

  const onRowClick = (row: Row<SalesReportResultDto>) => {
    if (row.original.sale_order_url) {
      window.open(row.original.sale_order_url, "_blank");
    }
  };

  return (
    <>
      <h2 className="text-3xl font-bold mb-4 text-gray-800">Sales Report</h2>
      <div className="bg-white p-4 rounded-md border border-gray-200 grid md:grid-cols-12 gap-4 mb-4 items-start">
        <div className="md:col-span-3 *:w-full">
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            Start Date
          </label>
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date)}
            dateFormat="dd MMM yyyy"
            placeholderText="Select Date"
            className="mt-1 block w-full rounded-[4px] border border-gray-200 focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>

        <div className="md:col-span-3 *:w-full">
          <label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            End Date
          </label>
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date)}
            dateFormat="dd MMM yyyy"
            placeholderText="Select Date"
            className="mt-1 block w-full rounded-[4px] border border-gray-200 focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
          />
        </div>

        <div className="md:col-span-3">
          <label
            htmlFor="branchSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Branch
          </label>
          <BranchSelector
            id="branchSelector"
            value={selectedBranches}
            onChange={setSelectedBranches}
          />
        </div>

        <div className="md:col-span-3">
          <label
            htmlFor="vendorSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Vendor
          </label>
          <PartnerSelector
            id="vendorSelector"
            placeholder="All Vendors"
            type="supplier"
            value={selectedVendors}
            onChange={setSelectedVendors}
          />
        </div>

        <div className="md:col-span-3">
          <label
            htmlFor="partnerSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Partner
          </label>
          <PartnerSelector
            id="partnerSelector"
            placeholder="All Partners"
            type="customer"
            value={selectedPartners}
            onChange={setSelectedPartners}
          />
        </div>

        <div className="md:col-span-3">
          <label
            htmlFor="productSelector"
            className="block text-sm font-medium text-gray-700"
          >
            Product
          </label>
          <ProductSelector
            selectedProducts={selectedProducts}
            onChange={setSelectedProducts}
          />
        </div>

        <div className="md:col-span-3">
          <label
            htmlFor="rangeSelector"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Profit Range
          </label>
          <RangeFilter
            className="rounded-[4px] w-full font-normal"
            templates={rangeOptions}
            value={selectedRange}
            onChange={setSelectedRange}
          />
        </div>
      </div>

      <dl
        className={cn(
          "mb-4 grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2",
        )}
      >
        {statsLoading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div
              key={`pulse-${i}`}
              className="h-32 rounded-md bg-gray-200/90 animate-pulse"
            ></div>
          ))
        ) : stats ? (
          <>
            <SalesCard data={stats} />
            <RevenueCard data={stats} />
          </>
        ) : null}
      </dl>

      <div className="bg-white rounded-md border border-gray-200 border-collapse">
        {error ? (
          <div className="min-h-[40vh] flex flex-col items-center justify-center gap-2 p-4">
            <ErrorState className="h-40 w-auto" />
            <p className="text-gray-500 text-center">
              Oops.. Something went wrong <br />
              Try generate the report again
            </p>

            <button
              type="button"
              onClick={() => mutate()}
              className="mt-4 disabled:cursor-not-allowed bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={!selectedCompany || !startDate || !endDate || isLoading}
            >
              {isLoading ? "Reloading..." : "Reload"}
            </button>
          </div>
        ) : !isFetched ? (
          <div className="min-h-[40vh] flex flex-col items-center justify-center gap-2 p-4">
            <EmptyState className="h-40 w-auto" />
            <p className="text-gray-500 text-center">
              No data available for the selected criteria. <br />
              Please generate the report.
            </p>

            <button
              type="button"
              onClick={fetchData}
              className="mt-4 disabled:cursor-not-allowed bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={!selectedCompany || !startDate || !endDate || isLoading}
            >
              {isLoading ? "Generating..." : "Generate"}
            </button>
          </div>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={data?.data || []}
              pagination={paginationState}
              rowCount={data?.pagination.totalCount || 0}
              onPaginationChange={setPaginationState}
              loading={isLoading}
              className="rounded-none border-x-0"
              onRowClick={onRowClick}
              toolbars={({ table }) => (
                <div className="pt-4 px-4 pb-2 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-600">
                    Showing{" "}
                    <span className="text-gray-800">{table.getRowCount()}</span>{" "}
                    results
                  </div>
                  <div className="flex items-center gap-2">
                    {data?.data && data.data.length > 0 && (
                      <button
                        type="button"
                        disabled={
                          exporting ||
                          !selectedCompany ||
                          !startDate ||
                          !endDate
                        }
                        onClick={handleExport}
                        className={cn(
                          "flex items-center justify-center gap-2 h-10 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium",
                          "disabled:bg-green-600/40 disabled:cursor-not-allowed",
                          exporting && "cursor-progress",
                        )}
                      >
                        <ArrowDownTrayIcon className="size-4" />
                        {exporting ? "Exporting..." : "Export"}
                      </button>
                    )}
                    <DataTable.ColumnToggle />
                  </div>
                </div>
              )}
            />
          </>
        )}
      </div>
    </>
  );
}
